import * as Sentry from "@sentry/nextjs";

/**
 * Sentry 运行时工具层。
 *
 * 这个文件不负责初始化 Sentry，初始化分别在：
 * - instrumentation-client.ts
 * - sentry.server.config.ts
 * - sentry.edge.config.ts
 *
 * 这里负责的是“上报规范”：
 * 1. 给业务代码提供统一的 captureError / captureMessage 入口
 * 2. 统一处理 level、tags、context、fingerprint，避免各处上报格式不一致
 * 3. 给 HTTP/接口错误提供专门的分类方法 captureHttpError
 * 4. 让 route error、global error、axios 拦截器都走同一套上报协议
 *
 * 这样做的目的：
 * - 让 Sentry 里的 issue 更容易筛选和分组
 * - 减少“同类错误被上报成多种格式”的混乱
 * - 后续如果要加 beforeSend 过滤、统一 tags、用户上下文，也只需要改这一层
 */

// 这里统一约束 Sentry 的 level，避免业务侧随手传任意字符串。
type SentryLevel = "fatal" | "error" | "warning" | "log" | "info" | "debug";
type SentryTracePropagationTarget = string | RegExp;

// 所有手动上报都走同一份 options 协议：
// - tags 用来做筛选和聚合
// - extra/context 用来带排查信息
// - fingerprint 用来控制 Sentry 分组
type CaptureOptions = {
    level?: SentryLevel;
    tags?: Record<string, string | number | boolean | null | undefined>;
    extra?: Record<string, unknown>;
    contextName?: string;
    fingerprint?: string[];
};

// Sentry tag 只能安全接收字符串，这里统一做一层收口，避免调用方每次手动处理。
function normalizeTags( tags?: CaptureOptions["tags"] ) {
    if ( !tags ) return undefined;

    return Object.fromEntries(
        Object.entries( tags ).filter( ( [ , value ] ) => value !== undefined && value !== null && value !== "" ).map( (
            [ key, value ],
        ) => [ key, String( value ) ] ),
    );
}

// 所有自定义上报都走同一个 scope 封装，确保 level/tag/context/fingerprint 行为一致。
function withScope<T>( options: CaptureOptions, callback: () => T ) {
    return Sentry.withScope( scope => {
        if ( options.level ) {
            scope.setLevel( options.level );
        }

        const tags = normalizeTags( options.tags );
        if ( tags ) {
            Object.entries( tags ).forEach( ( [ key, value ] ) => {
                scope.setTag( key, value );
            } );
        }

        // 对 extra/context 在入口层先做脱敏裁剪，形成"入口 + beforeSend 出口"双重保险。
        // 避免业务代码传入大对象或敏感字段直接进入 Sentry scope，在 beforeSend 之前就被 SDK 内部读取。
        const sanitizedExtra = options.extra ? sanitizeSentryValue( options.extra ) : undefined;
        if ( options.contextName && sanitizedExtra ) {
            scope.setContext( options.contextName, sanitizedExtra as Record<string, unknown> );
        } else if ( sanitizedExtra ) {
            Object.entries( sanitizedExtra ).forEach( ( [ key, value ] ) => {
                scope.setExtra( key, value );
            } );
        }

        // 如果没有自定义 fingerprint，按错误类型和级别自动归并，
        // 避免同一类问题因为不同 message 文案被拆成几千条 issue。
        if ( options.fingerprint?.length ) {
            scope.setFingerprint( options.fingerprint );
        } else {
            scope.setFingerprint( [
                options.level || "error",
                options.tags?.error_type != null ? String( options.tags.error_type ) : "unknown",
                options.tags?.error_kind != null ? String( options.tags.error_kind ) : "unknown",
            ] );
        }

        return callback();
    } );
}

const SENTRY_REDACT_KEY_PATTERN = /(password|passwd|authorization|cookie|token|secret|apikey|api_key)/i;
const SENTRY_MAX_STRING = 500;
const SENTRY_MAX_ARRAY = 20;
const SENTRY_MAX_DEPTH = 3;
function getUrlOrigin( value?: string ) {
    if ( !value ) {
        return undefined;
    }

    try {
        return new URL( value ).origin;
    } catch {
        try {
            return new URL( value, "https://placeholder.local" ).origin;
        } catch {
            return undefined;
        }
    }
}

export function getSentryTracePropagationTargets(): SentryTracePropagationTarget[] {
    const targets: SentryTracePropagationTarget[] = [ "localhost", /^\// ];
    const origins = new Set<string>();
    const envOrigins = [
        process.env.NEXT_PUBLIC_DOMAIN,
        process.env.NEXT_PUBLIC_IM_URL,
        process.env.NEXT_PUBLIC_SHARE_URL,
    ].map( getUrlOrigin ).filter( ( origin ): origin is string => Boolean( origin ) );

    envOrigins.forEach( origin => origins.add( origin ) );

    if ( typeof window !== "undefined" && window.location.origin ) {
        origins.add( window.location.origin );
    }

    origins.forEach( origin => {
        targets.push( origin );
    } );

    return targets;
}

// 对发往 Sentry 的附加上下文做统一裁剪与脱敏。
// 这一层是“最后一道保险”：
// - 业务代码可能在 extra/context 里塞入体积很大的对象
// - axios/错误边界也可能把敏感字段一路带过来
// 所以无论上游是否已经做过处理，这里都会再做一次兜底收口。
function sanitizeSentryValue( value: unknown, depth: number = 0 ): unknown {
    if ( depth > SENTRY_MAX_DEPTH ) {
        return "[TruncatedDepth]";
    }

    if ( value == null ) {
        return value;
    }

    if ( typeof value === "string" ) {
        return value.length > SENTRY_MAX_STRING
            ? `${value.slice( 0, SENTRY_MAX_STRING )}…(${value.length - SENTRY_MAX_STRING})`
            : value;
    }

    if ( typeof value === "number" || typeof value === "boolean" ) {
        return value;
    }

    if ( Array.isArray( value ) ) {
        const limited = value.slice( 0, SENTRY_MAX_ARRAY ).map( item => sanitizeSentryValue( item, depth + 1 ) );
        if ( value.length > SENTRY_MAX_ARRAY ) {
            limited.push( `[+${value.length - SENTRY_MAX_ARRAY} more]` );
        }
        return limited;
    }

    if ( typeof value === "object" ) {
        const sanitized: Record<string, unknown> = {};
        Object.entries( value as Record<string, unknown> ).forEach( ( [ key, item ] ) => {
            sanitized[key] = SENTRY_REDACT_KEY_PATTERN.test( key )
                ? "[REDACTED]"
                : sanitizeSentryValue( item, depth + 1 );
        } );
        return sanitized;
    }

    try {
        return String( value );
    } catch {
        return "[Unserializable]";
    }
}

// 从 event 和 hint 中抽取可读文本，供过滤器统一判断。
// beforeSend 在不同错误来源下拿到的数据形态不完全一致：
// - 普通 Error 可能在 event.message
// - 异常堆栈可能在 event.exception.values
// - 原始异常对象可能在 hint.originalException / syntheticException
// 这里先把它们收敛成字符串数组，后续过滤规则只处理一种输入。
function extractErrorStrings( event: any, hint: any ) {
    const values = [
        event?.message,
        ...( event?.exception?.values || [] ).flatMap( (
            entry: any,
        ) => [ entry?.value, entry?.type, entry?.message ] ),
        hint?.originalException?.message,
        hint?.syntheticException?.message,
    ];

    return values.filter( ( value: unknown ): value is string => typeof value === "string" );
}

// chunk 加载失败、浏览器常见噪音、请求取消、插件错误等低价值错误统一丢弃。
// 这类错误对定位业务问题价值低，且容易集中出现，在出口直接过滤可减少 15%~20% 的噪音。
// 注意：不在这里全局过滤 Network Error / Failed to fetch 等网络消息，
// 因为真正的 API 崩溃、CORS 错误、Gateway 错误也会表现为这类消息，应在调用层按 context 区分。
function isKnownNoiseError( texts: string[] ) {
    const knownLowValuePatterns = [
        /ChunkLoadError/i,
        /Loading chunk [\d]+ failed/i,

        // ResizeObserver 循环限制（项目中多处使用 ResizeObserver）
        /ResizeObserver loop limit exceeded/i,
        /ResizeObserver loop completed with undelivered notifications/i,

        // 请求取消（用户切换页面、主动取消、超时取消）
        /AbortError/i,
        /CanceledError/i,
        /The operation was aborted/i,
        /Request aborted/i,

        // 跨域/浏览器安全
        /Script error\.?/i,
        /Script Error/i,
        /Non-Error promise rejection captured/i,

        // 浏览器插件（广告拦截、翻译、密码管理器等）
        /chrome-extension:\/\//i,
        /moz-extension:\/\//i,
        /safari-extension:\/\//i,
        /webkit-masked-url:/i,

        // 旧浏览器/容器环境
        /window\.matchMedia is not a function/i,
        /Can't find variable: _tableau/i,
        /ethereum is not defined/i,
        /web3 is not defined/i,

        // 微信/支付宝/WebView 容器常见噪音
        /cordova_not_available/i,
    ];

    return knownLowValuePatterns.some( pattern => texts.some( text => pattern.test( text ) ) );
}

// 只过滤“事件所属页面 URL 明确不是当前站点”的错误。
// 注意这不是过滤所有第三方 SDK 错误：
// 如果第三方 SDK 跑在当前页面上下文里并抛出未捕获异常，request.url 仍然会是当前站点，依然会上报。
function isExternalHostError( event: any ) {
    const eventUrl = event?.request?.url;
    if ( typeof window !== "undefined" && typeof eventUrl === "string" && /^https?:/i.test( eventUrl ) ) {
        try {
            const resolved = new URL( eventUrl, window.location.origin );
            return Boolean( resolved.host && resolved.host !== window.location.host );
        } catch {
            return false;
        }
    }

    return false;
}

// 统一决定“这个事件要不要直接丢弃”。
// 这层判断放在 beforeSend，而不是散落在各个调用点，是为了保证：
// 1. 入口再多，过滤标准也只有一份
// 2. 未来补新的已知噪音时，只需要改一个地方
function shouldDropEvent( event: any, hint: any ) {
    const texts = extractErrorStrings( event, hint );
    if ( isKnownNoiseError( texts ) ) {
        return true;
    }

    if (
        typeof navigator !== "undefined" && navigator.onLine === false
        && texts.some( text => /Network Error/i.test( text ) )
    ) {
        return true;
    }

    if ( isExternalHostError( event ) ) {
        return true;
    }

    return false;
}

export function createSentryBeforeBreadcrumb() {
    return function beforeBreadcrumb( breadcrumb: any ) {
        // 低价值 console.log 不进入 breadcrumb，避免把真正的错误上下文淹没。
        if ( breadcrumb?.category === "console" && breadcrumb?.level === "log" ) {
            return null;
        }

        // 成功的网络请求默认不保留，避免 breadcrumb 里全是 200 请求噪音。
        if (
            ( breadcrumb?.category === "xhr" || breadcrumb?.category === "fetch" )
            && typeof breadcrumb?.data?.status_code === "number"
            && breadcrumb.data.status_code < 400
        ) {
            return null;
        }

        return breadcrumb;
    };
}

export function createSentryBeforeSend() {
    return function beforeSend( event: any, hint: any ) {
        // 先做“是否值得保留”判断，再做裁剪。
        // 否则就算最终丢弃，也白白做了一轮深层处理。
        if ( shouldDropEvent( event, hint ) ) {
            return null;
        }

        // 统一裁剪各种上下文，避免同类问题因为不同调用点而带入不同体量的数据。
        if ( event?.extra ) {
            event.extra = sanitizeSentryValue( event.extra );
        }

        if ( event?.contexts ) {
            event.contexts = sanitizeSentryValue( event.contexts );
        }

        // request.data 经常最容易混入大对象或敏感内容，所以单独兜底处理。
        if ( event?.request?.data ) {
            event.request.data = sanitizeSentryValue( event.request.data );
        }

        return event;
    };
}

// 通用异常上报入口。
// 适合 ErrorBoundary、手动 try/catch、业务异常兜底等场景。
export function captureError( error: unknown, options: CaptureOptions = {} ) {
    return withScope( options, () => Sentry.captureException( error ) );
}

// 通用消息上报入口。
// 更适合“有问题但未必是异常对象”的场景，比如关键业务告警或状态提示。
export function captureMessage( message: string, options: CaptureOptions = {} ) {
    return withScope( options, () => Sentry.captureMessage( message ) );
}

// 提供一个最薄的 tag 设置封装，方便在业务代码里直接打全局标签。
export function setSentryTag( key: string, value: string ) {
    Sentry.setTag( key, value );
}

// 提供一个最薄的 context 设置封装，方便在业务代码里挂较结构化的上下文。
export function setSentryContext( name: string, context: Record<string, unknown> ) {
    Sentry.setContext( name, context );
}

// 设置 Sentry 用户上下文，用于错误溯源（谁出的问题）。
// 登录成功后调用，携带 userId 和 username，不要塞敏感字段（私钥、助记词、完整手机号）。
export function setSentryUser( user: { id: string | number; username?: string } | null ) {
    if ( user ) {
        Sentry.setUser( { id: String( user.id ), username: user.username } );
    } else {
        Sentry.setUser( null );
    }
}

// 统一描述 HTTP 场景下的错误信息。
// kind 的目的不是做“技术上是否是 axios error”判断，而是帮助 Sentry 里按问题类型筛选：
// - network: 网络失败、超时、断网
// - http: 非 2xx 或真正的服务端异常
// - biz: HTTP 成功但业务 code 失败
// - request_setup: 请求发送前的配置或拦截器异常
type HttpErrorMeta = {
    kind: "network" | "http" | "biz" | "request_setup";
    method?: string;
    url?: string;
    status?: number;
    code?: string | number;
    traceId?: string;
    message?: string;
    responseData?: unknown;
};

// 对接口返回做最小化裁剪。
// 目的不是“完整保留排查上下文”，而是“只留下足够判断问题类型的信息”：
// - 字符串响应最多截断到 200 个字符，避免把整段 HTML/错误页塞进 Sentry
// - 对象响应只保留 code / msg / message / status
// - 余额、订单、用户资料、支付地址等业务数据一律不在这里上传
//
// 这样可以降低两类风险：
// 1. 把大体积响应体塞进 Sentry，导致 issue 噪音和存储开销上升
// 2. 把原本不该离开业务系统的数据显式上传到第三方错误平台
function sanitizeHttpResponseData( responseData: unknown ) {
    if ( responseData == null ) {
        return undefined;
    }

    if ( typeof responseData === "string" ) {
        return responseData.length > 200 ? `${responseData.slice( 0, 200 )}...` : responseData;
    }

    if ( typeof responseData !== "object" ) {
        return responseData;
    }

    const source = responseData as Record<string, unknown>;
    const sanitized: Record<string, unknown> = {};

    [ "code", "msg", "message", "status" ].forEach( key => {
        if ( source[key] !== undefined ) {
            sanitized[key] = source[key];
        }
    } );

    return Object.keys( sanitized ).length > 0 ? sanitized : undefined;
}

// fingerprint 只使用接口 path，不使用 query/hash。
// 否则同一个接口因为时间戳、分页参数、追踪参数不同，会在 Sentry 里拆成很多 issue。
function normalizeHttpPath( rawUrl?: string ) {
    if ( !rawUrl ) {
        return "unknown";
    }

    try {
        const parsed = new URL( rawUrl, "http://localhost" );
        return parsed.pathname || rawUrl;
    } catch {
        return rawUrl.replace( /\?.*$/, "" ).replace( /#.*$/, "" );
    }
}

// HTTP 错误统一打上 error_type=http，后续在 Sentry 里可直接按网络/业务/配置错误筛选。
export function captureHttpError( meta: HttpErrorMeta, error?: unknown ) {
    const method = meta.method?.toUpperCase();
    const normalizedPath = normalizeHttpPath( meta.url );

    // 当原始错误对象信息不足时，兜底拼一个可读标题，保证 issue 至少可理解。
    const titleParts = [ method, meta.url, meta.message || meta.code ].filter( Boolean );
    const fallbackError = new Error( titleParts.join( " " ) || `HTTP ${meta.kind} error` );

    return captureError( error || fallbackError, {
        // 业务码失败通常是 warning，真正的网络/服务端异常按 error 处理。
        level: meta.kind === "biz" ? "warning" : "error",
        tags: {
            error_type: "http",
            error_kind: meta.kind,
            method: meta.method,
            url: normalizedPath,
            status: meta.status,
            biz_code: meta.code,
            trace_id: meta.traceId,
        },
        contextName: "http_error",
        // extra 里保留原始 URL，方便排查具体请求；
        // fingerprint 里使用 normalizedPath，避免 query 参数导致 issue 过度分裂。
        extra: {
            method: meta.method,
            url: meta.url,
            status: meta.status,
            code: meta.code,
            traceId: meta.traceId,
            message: meta.message,
            responseData: sanitizeHttpResponseData( meta.responseData ),
        },
        // 分组策略优先按“错误类型 + 方法 + URL + 状态/业务码”归并，
        // 这样同一个接口的同类错误会聚到一起，不会因为 message 文案细节过度分裂。
        fingerprint: [
            "http",
            meta.kind,
            method || "UNKNOWN",
            normalizedPath,
            String( meta.status || meta.code || "unknown" ),
        ],
    } );
}

/**
 * 前端监控统一入口。
 *
 * 用于统一管理：
 * - Sentry 错误上报
 * - 错误等级划分
 * - 业务标签注入
 * - 网络异常标准化
 * - 告警分类与后续治理
 *
 * 设计目标：
 * - 禁止业务侧直接调用 Sentry.captureException
 * - 统一错误结构、severity、tags、fingerprint
 * - 降低 Sentry 噪音
 * - 便于后续接入：
 *   - Metrics
 *   - Replay
 *   - OpenTelemetry
 *   - AI 排障/MCP
 *   - 告警平台
 *
 * 错误等级说明：
 *
 * fatal:
 * - 页面白屏
 * - React Root 崩溃
 * - SSR 崩溃
 * - 核心运行时异常
 * - 需要立即处理的问题
 *
 * error:
 * - 核心业务失败
 * - 钱包连接失败
 * - 交易失败
 * - 核心接口异常
 *
 * warning:
 * - 网络波动
 * - 非核心接口失败
 * - 可恢复问题
 * - 降级逻辑触发
 *
 * info/business:
 * - 用户主动取消
 * - 业务状态提示
 * - 权限不足
 * - 非异常业务流程
 *
 * 注意：
 * - 不建议直接上报 console.error
 * - 高频网络错误建议采样
 * - 非核心业务错误建议使用 business/warn
 * - 所有异常建议携带 tags/module/page 等上下文
 */
export const monitor = {
    /**
     * 致命错误。
     *
     * 用于：
     * - 页面白屏
     * - Root 崩溃
     * - SSR 致命异常
     * - 导致页面不可用的问题
     *
     * 通常需要：
     * - 立即告警
     * - 高优先级处理
     */
    fatal( error: unknown, options?: Omit<CaptureOptions, "level"> ) {
        return captureError( error, { ...options, level: "fatal", tags: { ...options?.tags, severity: "fatal" } } );
    },

    /**
     * 核心业务错误。
     *
     * 用于：
     * - 交易失败
     * - 钱包连接失败
     * - 核心接口异常
     * - 关键流程执行失败
     */
    error( error: unknown, options?: Omit<CaptureOptions, "level"> ) {
        return captureError( error, { ...options, level: "error", tags: { ...options?.tags, severity: "error" } } );
    },

    /**
     * 警告级别异常。
     *
     * 用于：
     * - 网络波动
     * - 超时
     * - 降级逻辑
     * - 可恢复异常
     *
     * 一般不需要立即告警。
     */
    warn( message: string, options?: Omit<CaptureOptions, "level"> ) {
        return captureMessage( message, {
            ...options,
            level: "warning",
            tags: { ...options?.tags, severity: "warning" },
        } );
    },

    /**
     * 网络异常统一入口。
     *
     * 用于统一处理：
     * - axios/fetch 错误
     * - HTTP 状态码异常
     * - 超时
     * - 弱网
     * - 请求取消
     *
     * 内部可统一实现：
     * - 采样
     * - URL 白名单
     * - 指纹聚合
     * - 噪音过滤
     */
    network( meta: HttpErrorMeta, error?: unknown ) {
        return captureHttpError( meta, error );
    },

    /**
     * 业务提示类事件。
     *
     * 用于：
     * - 用户主动取消
     * - 权限不足
     * - 状态校验失败
     * - 余额不足
     * - 非异常业务流程
     *
     * 默认归类为 info，
     * 用于业务观测而非错误告警。
     */
    business( message: string, options?: Omit<CaptureOptions, "level"> ) {
        return captureMessage( message, {
            ...options,
            level: "info",
            tags: { ...options?.tags, severity: "info", error_type: "business" },
        } );
    },
};
